import { supabase, type Database } from '../lib/supabase'
import type { Vehicle, CreateVehicleRequest, UpdateVehicleRequest } from '../types/vehicle'
import type { SearchFilters } from '../types/search'
import { validatePhoneNumber } from '../utils/validation'
import { apiCall } from '../utils/networkErrorHandler'
import { logServiceError } from '../utils/errorLogger'

type VehicleRow = Database['public']['Tables']['vehicles']['Row']
type VehicleInsert = Database['public']['Tables']['vehicles']['Insert']
type VehicleUpdate = Database['public']['Tables']['vehicles']['Update']

export class VehicleService {
  /**
   * Convert database row to Vehicle interface
   */
  private mapRowToVehicle(row: VehicleRow): Vehicle {
    return {
      id: row.id,
      registrationNumber: row.registration_number,
      vehicleType: row.vehicle_type,
      brandModel: row.brand_model,
      seatingCapacity: row.seating_capacity,
      status: row.status,
      imageUrl: row.image_url || undefined,
      createdAt: new Date(row.created_at),
      updatedAt: new Date(row.updated_at)
    }
  }

  /**
   * Convert Vehicle interface to database insert format
   */
  private mapVehicleToInsert(vehicle: CreateVehicleRequest): VehicleInsert {
    return {
      registration_number: vehicle.registrationNumber,
      vehicle_type: vehicle.vehicleType,
      brand_model: vehicle.brandModel,
      seating_capacity: vehicle.seatingCapacity,
      status: vehicle.status || 'available',
      image_url: vehicle.imageUrl || null
    }
  }

  /**
   * Convert Vehicle interface to database update format
   */
  private mapVehicleToUpdate(vehicle: UpdateVehicleRequest): VehicleUpdate {
    const update: VehicleUpdate = {}

    if (vehicle.registrationNumber !== undefined) {
      update.registration_number = vehicle.registrationNumber
    }
    if (vehicle.vehicleType !== undefined) {
      update.vehicle_type = vehicle.vehicleType
    }
    if (vehicle.brandModel !== undefined) {
      update.brand_model = vehicle.brandModel
    }
    if (vehicle.seatingCapacity !== undefined) {
      update.seating_capacity = vehicle.seatingCapacity
    }
    if (vehicle.status !== undefined) {
      update.status = vehicle.status
    }
    if (vehicle.imageUrl !== undefined) {
      update.image_url = vehicle.imageUrl || null
    }

    return update
  }

  /**
   * Create a new vehicle
   * Requirements: 1.1 - Store vehicle with all required fields
   */
  async createVehicle(vehicleData: CreateVehicleRequest): Promise<Vehicle> {
    return apiCall(async () => {
      // Validate required fields
      if (!vehicleData.registrationNumber?.trim()) {
        throw new Error('Registration number is required')
      }
      if (!vehicleData.vehicleType?.trim()) {
        throw new Error('Vehicle type is required')
      }
      if (!vehicleData.brandModel?.trim()) {
        throw new Error('Brand/model is required')
      }
      if (!vehicleData.seatingCapacity || vehicleData.seatingCapacity <= 0) {
        throw new Error('Seating capacity must be a positive number')
      }

      const insertData = this.mapVehicleToInsert(vehicleData)
      
      const { data, error } = await supabase
        .from('vehicles')
        .insert(insertData)
        .select()
        .single()

      if (error) {
        // Handle unique constraint violation for registration number
        if (error.code === '23505' && error.message.includes('registration_number')) {
          const uniqueError = new Error('A vehicle with this registration number already exists')
          logServiceError(uniqueError, 'VehicleService', 'createVehicle', vehicleData)
          throw uniqueError
        }
        const serviceError = new Error(`Failed to create vehicle: ${error.message}`)
        logServiceError(serviceError, 'VehicleService', 'createVehicle', vehicleData)
        throw serviceError
      }

      return this.mapRowToVehicle(data)
    }, 'create vehicle')
  }

  /**
   * Get vehicle by ID
   */
  async getVehicleById(id: string): Promise<Vehicle | null> {
    const { data, error } = await supabase
      .from('vehicles')
      .select('*')
      .eq('id', id)
      .single()

    if (error) {
      if (error.code === 'PGRST116') {
        return null // Vehicle not found
      }
      throw new Error(`Failed to get vehicle: ${error.message}`)
    }

    return this.mapRowToVehicle(data)
  }

  /**
   * Get all vehicles
   */
  async getAllVehicles(): Promise<Vehicle[]> {
    const { data, error } = await supabase
      .from('vehicles')
      .select('*')
      .order('created_at', { ascending: false })

    if (error) {
      throw new Error(`Failed to get vehicles: ${error.message}`)
    }

    return data.map(row => this.mapRowToVehicle(row))
  }

  /**
   * Update vehicle information
   * Requirements: 1.2 - Modify existing record and maintain data integrity
   */
  async updateVehicle(id: string, vehicleData: UpdateVehicleRequest): Promise<Vehicle> {
    // Validate fields if provided
    if (vehicleData.registrationNumber !== undefined && !vehicleData.registrationNumber.trim()) {
      throw new Error('Registration number cannot be empty')
    }
    if (vehicleData.vehicleType !== undefined && !vehicleData.vehicleType.trim()) {
      throw new Error('Vehicle type cannot be empty')
    }
    if (vehicleData.brandModel !== undefined && !vehicleData.brandModel.trim()) {
      throw new Error('Brand/model cannot be empty')
    }
    if (vehicleData.seatingCapacity !== undefined && vehicleData.seatingCapacity <= 0) {
      throw new Error('Seating capacity must be a positive number')
    }

    const updateData = this.mapVehicleToUpdate(vehicleData)
    
    const { data, error } = await supabase
      .from('vehicles')
      .update(updateData)
      .eq('id', id)
      .select()
      .single()

    if (error) {
      // Handle unique constraint violation for registration number
      if (error.code === '23505' && error.message.includes('registration_number')) {
        throw new Error('A vehicle with this registration number already exists')
      }
      if (error.code === 'PGRST116') {
        throw new Error('Vehicle not found')
      }
      throw new Error(`Failed to update vehicle: ${error.message}`)
    }

    return this.mapRowToVehicle(data)
  }

  /**
   * Delete vehicle
   * Requirements: 1.3 - Remove vehicle record and handle schedule dependencies
   */
  async deleteVehicle(id: string): Promise<void> {
    // Check if vehicle exists in schedule entries
    const { data: scheduleEntries, error: scheduleError } = await supabase
      .from('schedule_entries')
      .select('id')
      .eq('vehicle_id', id)
      .limit(1)

    if (scheduleError) {
      throw new Error(`Failed to check vehicle dependencies: ${scheduleError.message}`)
    }

    if (scheduleEntries && scheduleEntries.length > 0) {
      throw new Error('Cannot delete vehicle: it has existing schedule entries')
    }

    const { error } = await supabase
      .from('vehicles')
      .delete()
      .eq('id', id)

    if (error) {
      throw new Error(`Failed to delete vehicle: ${error.message}`)
    }
  }

  /**
   * Search and filter vehicles
   * Requirements: 1.4 - Support vehicle search and filtering
   */
  async searchVehicles(filters: SearchFilters): Promise<Vehicle[]> {
    let query = supabase
      .from('vehicles')
      .select('*')

    // Apply registration number filter (partial match)
    if (filters.vehicleRegistration?.trim()) {
      query = query.ilike('registration_number', `%${filters.vehicleRegistration.trim()}%`)
    }

    // Apply vehicle ID filter (exact match)
    if (filters.vehicleId?.trim()) {
      query = query.eq('id', filters.vehicleId.trim())
    }

    query = query.order('created_at', { ascending: false })

    const { data, error } = await query

    if (error) {
      throw new Error(`Failed to search vehicles: ${error.message}`)
    }

    return data.map(row => this.mapRowToVehicle(row))
  }

  /**
   * Get vehicles by status
   */
  async getVehiclesByStatus(status: 'available' | 'in_use'): Promise<Vehicle[]> {
    const { data, error } = await supabase
      .from('vehicles')
      .select('*')
      .eq('status', status)
      .order('created_at', { ascending: false })

    if (error) {
      throw new Error(`Failed to get vehicles by status: ${error.message}`)
    }

    return data.map(row => this.mapRowToVehicle(row))
  }

  /**
   * Check if registration number is unique
   * Requirements: 1.4 - Validate unique registration numbers
   */
  async isRegistrationNumberUnique(registrationNumber: string, excludeId?: string): Promise<boolean> {
    let query = supabase
      .from('vehicles')
      .select('id')
      .eq('registration_number', registrationNumber)

    if (excludeId) {
      query = query.neq('id', excludeId)
    }

    const { data, error } = await query

    if (error) {
      throw new Error(`Failed to check registration number uniqueness: ${error.message}`)
    }

    return data.length === 0
  }
}

// Export singleton instance
export const vehicleService = new VehicleService()